/**
 * Подгонка линий тренда для графиков
 * ===================================
 *
 * Чистые функции МНК: линейная (y = a + b·x) и степенная (y = a·x^b).
 * R² считается в исходном пространстве Y для обеих моделей, поэтому их
 * качество можно сравнивать напрямую.
 */

const MIN_POINTS = 3;

function rSquared(points, predict) {
  const mean = points.reduce((sum, p) => sum + p.y, 0) / points.length;
  let ssRes = 0;
  let ssTot = 0;

  points.forEach(({ x, y }) => {
    const residual = y - predict(x);
    ssRes += residual * residual;
    ssTot += (y - mean) * (y - mean);
  });

  if (ssTot === 0) return ssRes === 0 ? 1 : 0;
  return 1 - ssRes / ssTot;
}

function ordinaryLeastSquares(points) {
  const n = points.length;
  const sumX = points.reduce((sum, p) => sum + p.x, 0);
  const sumY = points.reduce((sum, p) => sum + p.y, 0);
  const sumXY = points.reduce((sum, p) => sum + p.x * p.y, 0);
  const sumXX = points.reduce((sum, p) => sum + p.x * p.x, 0);

  const denominator = n * sumXX - sumX * sumX;
  if (denominator === 0) return null;

  const slope = (n * sumXY - sumX * sumY) / denominator;
  const intercept = (sumY - slope * sumX) / n;
  return { slope, intercept };
}

function validPoints(points) {
  return points.filter(
    (p) => Number.isFinite(p.x) && Number.isFinite(p.y)
  );
}

/**
 * Линейный тренд y = a + b·x
 * @param {Array<{x: number, y: number}>} rawPoints
 * @returns {{type: string, params: object, r2: number, predict: function}|null}
 */
export function fitLinear(rawPoints) {
  const points = validPoints(rawPoints);
  if (points.length < MIN_POINTS) return null;

  const fit = ordinaryLeastSquares(points);
  if (!fit) return null;

  const predict = (x) => fit.intercept + fit.slope * x;
  return {
    type: 'linear',
    params: { a: fit.intercept, b: fit.slope },
    r2: rSquared(points, predict),
    predict
  };
}

/**
 * Степенной тренд y = a·x^b, подгонка МНК по ln y = ln a + b·ln x.
 * Точки с x <= 0 или y <= 0 отбрасываются — логарифм не определён.
 */
export function fitPower(rawPoints) {
  const points = validPoints(rawPoints).filter((p) => p.x > 0 && p.y > 0);
  if (points.length < MIN_POINTS) return null;

  const fit = ordinaryLeastSquares(
    points.map((p) => ({ x: Math.log(p.x), y: Math.log(p.y) }))
  );
  if (!fit) return null;

  const a = Math.exp(fit.intercept);
  const b = fit.slope;
  const predict = (x) => (x > 0 ? a * Math.pow(x, b) : null);

  return {
    type: 'power',
    params: { a, b },
    r2: rSquared(points, (x) => predict(x) ?? 0),
    predict
  };
}

const FITTERS = {
  linear: fitLinear,
  power: fitPower
};

export const TREND_MODELS = Object.keys(FITTERS);

export function fitTrend(model, points) {
  const fitter = FITTERS[model];
  return fitter ? fitter(points) : null;
}
